"use client";

import { JSX, useMemo, useState } from "react";
import {
  type LucideIcon,
  Euro,
  Users,
  UserPlus,
  Activity,
  Clock,
  Globe,
  Zap,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { CascadeIn } from "@/components/motion/cascade-in";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import { useHoverSelect } from "@/lib/hooks/use-hover-select";
import { useViewerLanguage } from "@/lib/i18n";
import type { ModuleMetricListData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";
import { resolveCategories, resolveCategoryColor } from "./shared/category-resolve";
import { type ListVariant, type TrendDirection } from "./shared/lists";

const ICON_MAP: Record<string, LucideIcon> = {
  euro: Euro,
  users: Users,
  "user-plus": UserPlus,
  activity: Activity,
  clock: Clock,
  globe: Globe,
  zap: Zap,
  "check-circle": CheckCircle,
  "alert-triangle": AlertTriangle,
};

type ResolvedMetric = {
  id: string | number;
  icon: LucideIcon;
  iconName: string;
  tint: string;
  label: string;
  value: string;
  trend: TrendDirection;
  pct: number;
  categoryId?: string;
  index: number;
};

type MetricGroup = {
  id: string;
  label: string;
  color: string | null;
  metrics: ResolvedMetric[];
};

const ALL = "__all__";

const GRID_CLASS =
  "grid grid-flow-col auto-cols-[minmax(220px,auto)] w-max pr-10 border-t border-l border-dashed border-[var(--border-dashed)]";

const SCROLL_CLASS =
  "pb-2 [mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)]";

function rowsClass(count: number): string {
  if (count <= 1) return "grid-rows-1";
  if (count <= 2) return "grid-rows-2";
  return "grid-rows-3";
}

function trendMeta(trend: TrendDirection, grayscale?: boolean): { Icon: LucideIcon; name: string; color: string } {
  if (trend === "up") return { Icon: TrendingUp, name: "trending-up", color: grayscale ? "var(--fg)" : "var(--emerald)" };
  if (trend === "down") return { Icon: TrendingDown, name: "trending-down", color: grayscale ? "var(--muted)" : "var(--rose)" };
  return { Icon: Minus, name: "minus", color: "var(--subtle)" };
}

export default function ModuleMetricList({
  data,
  variant = "icon",
  grayscale,
  accent,
  className,
}: {
  data: ModuleMetricListData;
  variant?: ListVariant;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const metrics = useMemo<ResolvedMetric[]>(
    () =>
      data.metrics.map((m, index) => ({
        ...m,
        icon: ICON_MAP[m.icon] ?? Activity,
        iconName: m.icon in ICON_MAP ? m.icon : "activity",
        index,
      })),
    [data.metrics],
  );

  const { t } = useViewerLanguage();
  const restLabel = t("Other");
  const row = useHoverSelect<string | number>();
  const effective = row.effective;
  const hasHover = useHoverCapable();

  const resolved = useMemo(
    () => resolveCategories(data.categories, []),
    [data.categories],
  );
  const free = resolved.free;

  const groups = useMemo<MetricGroup[]>(() => {
    if (!free) return [];
    const known = resolved.categories.map((cat) => ({
      id: cat.id,
      label: cat.label,
      color: cat.color,
      metrics: metrics.filter((m) => m.categoryId === cat.id),
    }));
    const knownIds = new Set(resolved.categories.map((cat) => cat.id));
    const rest = metrics.filter(
      (m) => !m.categoryId || !knownIds.has(m.categoryId),
    );
    return [
      ...known.filter((g) => g.metrics.length > 0),
      ...(rest.length > 0
        ? [{ id: "__rest", label: restLabel, color: null, metrics: rest }]
        : []),
    ];
  }, [free, metrics, resolved, restLabel]);

  const [active, setActive] = useState<string>(ALL);

  const shown = useMemo(
    () =>
      active === ALL
        ? metrics
        : (groups.find((g) => g.id === active)?.metrics ?? metrics),
    [active, metrics, groups],
  );

  const renderMetric = (m: ResolvedMetric) => {
    const IconCmp = m.icon;
    const isHovered = effective === m.id;
    const dim = effective !== null && !isHovered;
    const { Icon: TrendCmp, name: trendName, color: trendColor } = trendMeta(m.trend, grayscale);
    const markerTone = free
      ? resolveCategoryColor(m.categoryId, resolved.categories, m.tint)
      : m.tint;

    return (
      <button
        key={m.id}
        type="button"
        aria-pressed={row.selected === m.id}
        className="w-full py-2 px-3 text-left cursor-pointer border-r border-b border-dashed border-[var(--border-dashed)]"
        style={{
          opacity: dim ? 0.4 : 1,
          transition: "opacity 0.15s, background 0.15s",
        }}
        onMouseEnter={hasHover ? () => row.setHovered(m.id) : undefined}
        onMouseLeave={hasHover ? () => row.setHovered(null) : undefined}
        onFocus={hasHover ? () => row.setHovered(m.id) : undefined}
        onBlur={hasHover ? () => row.setHovered(null) : undefined}
        onClick={(e) =>
          row.select(m.id, {
            kind: "row",
            id: m.id,
            element: e.currentTarget,
          })
        }
      >
        <div className="flex items-center gap-1.5">
          {variant === "icon" ? (
            <div className="shrink-0" style={{ color: markerTone }}>
              <AnimatedIcon name={m.iconName} fallback={IconCmp} size={11} strokeWidth={2} />
            </div>
          ) : (
            <span
              className="text-[10px] tabular-nums text-[var(--subtle)] font-medium shrink-0"
              style={{ minWidth: "16px" }}
            >
              {String(m.index + 1).padStart(2, "0")}
            </span>
          )}
          <span className="text-[11px] font-medium text-[var(--muted)] truncate">
            {stripInlineMarkdown(m.label)}
          </span>
        </div>

        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-[15px] tabular-nums text-[var(--fg)] font-medium">
            {m.value}
          </span>
          <span
            className="flex items-center gap-0.5 text-[10px] tabular-nums shrink-0"
            style={{ color: trendColor }}
          >
            <AnimatedIcon name={trendName} fallback={TrendCmp} size={10} strokeWidth={2} />
            {m.pct}%
          </span>
        </div>
      </button>
    );
  };

  if (!free) {
    return (
      <div className={cn("w-full", className)} style={importsTokenScope(grayscale, accent)}>
        <SwipeScroll className={SCROLL_CLASS}>
          <CascadeIn
            keySignature="metric-list"
            className={cn(GRID_CLASS, "grid-rows-3")}
          >
            {metrics.map(renderMetric)}
          </CascadeIn>
        </SwipeScroll>
      </div>
    );
  }

  return (
    <div
      className={cn("flex w-full flex-col gap-4", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      {groups.length > 1 && (
        <div className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <SegmentTabs
            value={active}
            onValueChange={(v) => {
              if (v === null) return;
              setActive(v);
              row.setHovered(null);
              row.setSelected(null);
            }}
            options={[
              { value: ALL, label: t("All"), count: metrics.length },
              ...groups.map((group) => ({
                value: group.id,
                label: stripInlineMarkdown(group.label),
                count: group.metrics.length,
                leading: group.color ? (
                  <span
                    className="size-1.5 rounded-full"
                    style={{ background: group.color }}
                  />
                ) : undefined,
              })),
            ]}
          />
        </div>
      )}
      <SwipeScroll className={SCROLL_CLASS}>
        <CascadeIn
          keySignature={`metric-list-${active}`}
          className={cn(GRID_CLASS, rowsClass(shown.length))}
        >
          {shown.map(renderMetric)}
        </CascadeIn>
      </SwipeScroll>
    </div>
  );
}
